import type { LeadContract } from "@/lib/types";
import { formatDateTime } from "@/lib/utils";

/**
 * What the record's Contract card says about each press of Send contract (ACL-427): the press as a
 * status a rep reads at a glance, and the steps it went through with their times, so nobody has to
 * open the contract app to see whether an agreement went out, was opened or was signed.
 *
 * The steps follow the ticket's list: requested (the press), generated (the agreement the contract
 * service made), sent (the link, by text and by DocuSeal's e-mail), opened, then signed, declined or
 * expired as the contract service reports them (ACL-425 in platform). An e-mail that bounced or was
 * marked as spam is its own step after sent (ACL-501), so the rep sees it. A press that failed stops
 * after the request with why; a dry run made nothing.
 */

export type ContractTone = "neutral" | "accent" | "success" | "warning" | "danger";

const STATUS: Record<string, { label: string; tone: ContractTone }> = {
  sent: { label: "Sent", tone: "accent" },
  viewed: { label: "Opened", tone: "accent" },
  signed: { label: "Signed", tone: "success" },
  declined: { label: "Declined", tone: "danger" },
  expired: { label: "Expired", tone: "warning" },
  failed: { label: "Failed", tone: "danger" },
  dry_run: { label: "Checked only", tone: "neutral" },
};

/** A press's statuses that say the lead opened the agreement. */
const OPENED = new Set<string>(["viewed", "signed", "declined"]);

/** A press's statuses once it made an agreement in DocuSeal: whatever became of it after. */
const MADE = new Set<string>(["sent", "viewed", "signed", "declined", "expired"]);

/**
 * How the card orders a lead's presses, given newest first as core-api lists them. It leads with
 * the newest press that made an agreement, as the contract app lists agreements and not refusals,
 * so a press refused after it (a duplicate, the service down) never hides an agreement the lead
 * has, or has signed; with none, with the newest press. `later` is the newest press after the one
 * it leads with, said beneath it so a refusal is not lost either. `others` are every other press,
 * newest first, and `newer` says whether one of them came after it.
 */
export function contractCard(
  rows: LeadContract[],
): { lead: LeadContract; later: LeadContract | null; others: LeadContract[]; newer: boolean } | null {
  if (rows.length === 0) return null;
  const index = Math.max(0, rows.findIndex((row) => MADE.has(row.status)));
  return {
    lead: rows[index]!,
    later: index > 0 ? rows[0]! : null,
    others: rows.filter((_, i) => i !== index),
    newer: index > 0,
  };
}

/** A press after the one the card leads with, as one line: when, and what came of it. */
export function laterStep(row: LeadContract): ContractStep {
  if (row.status === "failed") {
    return {
      label: "Pressed again",
      at: row.created_at,
      detail: row.error_detail || "The contract service did not make the agreement.",
      state: "bad",
    };
  }
  return { label: "Pressed again", at: row.created_at, detail: contractStatus(row).label, state: "open" };
}

/** The press's status as its badge says it. A sent agreement whose link reached the lead by
 *  neither text nor e-mail says so, as its step does: they cannot sign what they never got. A status
 *  this console does not know shows as it came. */
export function contractStatus(row: LeadContract): { label: string; tone: ContractTone } {
  if (row.status === "sent" && !contractDelivery(row).reached) {
    // A link kept for the lead's 8 am has not failed to reach them; it has not gone yet (ACL-630).
    return linkScheduled(row) ? { label: "Text scheduled", tone: "neutral" } : { label: "Not delivered", tone: "warning" };
  }
  return STATUS[row.status] ?? { label: row.status, tone: "neutral" };
}

/** `done`: it happened. `bad`: it went wrong, or did not happen and will not. `open`: not yet. */
export type ContractStepState = "done" | "bad" | "open";

export interface ContractStep {
  label: string;
  /** When it happened; null for a step that has not, or whose time is not kept. */
  at: string | null;
  /** What to say beside the time. */
  detail: string | null;
  state: ContractStepState;
}

/** `text` as a sentence: core-api's reasons mostly end in a full stop, and some do not. */
function sentence(text: string): string {
  const said = text.trim();
  return /[.!?]$/.test(said) ? said : `${said}.`;
}

/** Whether the press's link waits for the lead's 8 am to be texted (ACL-630). */
function linkScheduled(row: Pick<LeadContract, "texted" | "text_status">): boolean {
  return row.texted === true && row.text_status === "scheduled";
}

/** How the link reached the lead (ACL-423): by text, with the provider's last word on it, and by
 *  DocuSeal's own e-mail, with what DocuSeal said became of it (ACL-501). `reached` is false when
 *  neither got through: a bounced e-mail did not. */
export function contractDelivery(
  row: Pick<
    LeadContract,
    "texted" | "text_problem" | "text_status" | "text_failure" | "emailed" | "email_to" | "email_status"
  > &
    Partial<Pick<LeadContract, "text_scheduled_for">>,
): { detail: string; reached: boolean } {
  const parts: string[] = [];
  let reached = false;
  if (row.texted === true) {
    if (row.text_status === "failed") {
      parts.push(sentence(`Text not delivered: ${row.text_failure || "the provider could not deliver it"}`));
    } else if (row.text_status === "scheduled") {
      // Kept for the lead's 8 am (ACL-630): it has not reached them yet.
      parts.push(
        row.text_scheduled_for
          ? `Text scheduled for ${formatDateTime(row.text_scheduled_for)}, the lead's 8 am.`
          : "Text scheduled for the lead's 8 am.",
      );
    } else {
      parts.push(row.text_status === "delivered" ? "Texted, delivered." : "Texted.");
      reached = true;
    }
  } else if (row.texted === false) {
    parts.push(sentence(`Not texted: ${row.text_problem || "the text did not go out"}`));
  }
  if (row.emailed) {
    const to = row.email_to ? ` to ${row.email_to}` : "";
    if (row.email_status === "bounced") {
      parts.push(`E-mail${to} bounced.`);
    } else {
      // "Opened" on this card is the agreement; DocuSeal's open of the e-mail is said as the e-mail's.
      const said =
        row.email_status === "opened"
          ? "; the lead opened the e-mail"
          : row.email_status === "complaint"
            ? ", marked as spam"
            : "";
      parts.push(`E-mailed${to}${said}.`);
      reached = true;
    }
  }
  if (parts.length === 0) return { detail: "Not texted or e-mailed.", reached: false };
  return { detail: parts.join(" "), reached };
}

/** An e-mail that went wrong, as its own step (ACL-501): a bounce or a spam flag. Null when the
 *  e-mail is fine or there was none. While the agreement waits on the lead, a bounce says what still
 *  reaches them: the text, or the signing link sent another way, as the send dialog offers when the
 *  text did not go out. A second press is never offered: the agreement is live, so it is refused as a
 *  duplicate. */
function emailStep(row: LeadContract): ContractStep | null {
  if (!row.emailed) return null;
  if (row.email_status === "bounced") {
    const texted = row.texted === true && row.text_status !== "failed" && row.text_status !== "scheduled";
    const next =
      row.status !== "sent"
        ? ""
        : texted
          ? " The text did."
          : row.signing_url
            ? " Send them the signing link below another way."
            : "";
    return {
      label: "E-mail bounced",
      at: row.email_bounced_at ?? null,
      detail: `DocuSeal's e-mail did not reach the lead.${next}`,
      state: "bad",
    };
  }
  if (row.email_status === "complaint") {
    return {
      label: "E-mail marked as spam",
      at: row.email_complaint_at ?? null,
      detail: "The lead marked DocuSeal's e-mail as spam.",
      state: "bad",
    };
  }
  return null;
}

/** The press's steps, in the order they happen. */
export function contractSteps(row: LeadContract): ContractStep[] {
  const steps: ContractStep[] = [{ label: "Requested", at: row.created_at, detail: null, state: "done" }];
  if (row.status === "failed") {
    steps.push({
      label: "Failed",
      at: row.updated_at,
      detail: row.error_detail || "The contract service did not make the agreement.",
      state: "bad",
    });
    return steps;
  }
  if (row.status === "dry_run") {
    steps.push({
      label: "Not sent",
      at: null,
      detail: "Live sending was off, so the agreement was only checked.",
      state: "open",
    });
    return steps;
  }
  // Made and sent within the press itself, so their time is the request's, said once.
  steps.push({ label: "Generated", at: null, detail: row.agreement, state: "done" });
  const delivery = contractDelivery(row);
  // An agreement the lead opened reached them somehow, as its badge says.
  const reached = delivery.reached || Boolean(row.viewed_at) || OPENED.has(row.status);
  // A link that waits for the lead's 8 am is still to go, not gone wrong (ACL-630).
  const waits = !reached && linkScheduled(row);
  steps.push({
    label: reached ? "Sent" : waits ? "Scheduled" : "Not delivered",
    at: null,
    detail: delivery.detail,
    state: reached ? "done" : waits ? "open" : "bad",
  });
  const emailProblem = emailStep(row);
  if (emailProblem) steps.push(emailProblem);
  const viewedAt = row.viewed_at ?? null;
  if (viewedAt) {
    steps.push({ label: "Opened", at: viewedAt, detail: null, state: "done" });
  } else if (row.status === "sent") {
    steps.push({ label: "Opened", at: null, detail: "Not yet", state: "open" });
  }
  if (row.status === "signed") {
    steps.push({ label: "Signed", at: row.signed_at ?? row.updated_at, detail: null, state: "done" });
  } else if (row.status === "declined") {
    steps.push({ label: "Declined", at: row.updated_at, detail: "The lead declined to sign.", state: "bad" });
  } else if (row.status === "expired") {
    steps.push({
      label: "Expired",
      at: row.updated_at,
      detail: "The agreement expired before it was signed.",
      state: "bad",
    });
  } else {
    steps.push({ label: "Signed", at: null, detail: "Not yet", state: "open" });
  }
  return steps;
}
